import React, { useState } from 'react';
import { PrimaryButton, Card, ScreenHeader } from '../ui';
import gamesService from '../../../services/gamesService';
import { useGameEligibility } from '../useGameEligibility';
import GameLockedCard from '../GameLockedCard';
import GameTodayResultCard from '../GameTodayResultCard';
import { isValidGoogleShareLink } from '../../../utils/googleReviewLink';
import type { SpinResult } from '../../../types/games';

interface GoogleReviewGameProps {
  appName: string;
  outletId: number;
  onClose?: () => void;
}

const STEPS = [
  'Tap "Open Google Reviews" below to visit the restaurant on Google Maps.',
  'Sign in to your Google account if prompted.',
  'Rate the restaurant with stars (1–5).',
  'Write your review and submit it.',
  'Tap Share on your review and copy the link Google gives you.',
  'Paste that link below and tap Claim Reward.',
];

const GoogleReviewGame: React.FC<GoogleReviewGameProps> = ({ appName, outletId, onClose }) => {
  const { loading, eligible, eligibility } = useGameEligibility(appName, 'GOOGLE_REVIEW');
  const [userReviewLink, setUserReviewLink] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimResult, setClaimResult] = useState<SpinResult | null>(null);
  const [openedGoogle, setOpenedGoogle] = useState(false);

  const locked = !loading && !eligible;
  const misconfigured = eligibility?.reason === 'GOOGLE_URL_NOT_CONFIGURED';
  const rewardLabel = eligibility?.rewardLabel ?? 'a reward';

  function openGoogleReviews() {
    const url = eligibility?.googleReviewUrl;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpenedGoogle(true);
  }

  async function handleClaim() {
    setLinkError(null);
    setClaimError(null);
    const trimmed = userReviewLink.trim();
    if (!trimmed) {
      setLinkError('Paste the Google share link from your review.');
      return;
    }
    if (!isValidGoogleShareLink(trimmed)) {
      setLinkError('Enter a valid Google Maps share link (e.g. maps.app.goo.gl/...).');
      return;
    }

    setClaiming(true);
    try {
      const key = gamesService.lifetimeIdempotencyKey(outletId, 'GOOGLE_REVIEW');
      const res = await gamesService.claimGoogleReview(appName, key, trimmed);
      setClaimResult(res);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      if (message === 'REVIEW_ALREADY_CLAIMED') {
        setClaimError("You've already claimed a Google Review reward at this restaurant.");
      } else {
        setClaimError('Could not claim reward. Check your link and try again.');
      }
    } finally {
      setClaiming(false);
    }
  }

  return (
    <div style={{ background: 'var(--bg, #FFFFFF)', minHeight: '100vh', paddingBottom: 24 }}>
      <ScreenHeader
        title={<>Google <span style={{ color: '#4285F4' }}>Review</span></>}
        subtitle="Leave a review and earn your reward"
        onClose={onClose}
      />
      <div style={{ padding: '18px 24px 0' }}>
        {loading && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted, #9B8F6E)', marginTop: 40 }}>Loading…</p>
        )}

        {locked && !misconfigured && <GameLockedCard eligibility={eligibility} />}

        {misconfigured && (
          <Card style={{ textAlign: 'center', marginTop: 40 }}>
            <div style={{ fontSize: 32 }}>⚙️</div>
            <p style={{ color: 'var(--text-secondary, #6B5E3E)', marginTop: 8, fontSize: 14 }}>
              This game is not set up yet. Ask staff to configure the Google Review URL.
            </p>
          </Card>
        )}

        {!loading && eligible && !claimResult && (
          <>
            <Card style={{ textAlign: 'center', border: '1.5px solid var(--primary, #F5C21A)' }}>
              <div style={{ fontSize: 28 }}>⭐</div>
              <div style={{ fontWeight: 800, fontSize: 20, color: 'var(--primary-dark, #D4A416)', marginTop: 6 }}>
                Win {rewardLabel}
              </div>
              <p style={{ fontSize: 12.5, color: 'var(--text-secondary, #6B5E3E)', marginTop: 4 }}>
                Leave a Google review and claim your one-time reward
              </p>
            </Card>

            <div style={{ height: 16 }} />

            <Card>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>How to get your reward</div>
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--text-secondary, #6B5E3E)', lineHeight: 1.6 }}>
                {STEPS.map((step, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>{step}</li>
                ))}
              </ol>
            </Card>

            <div style={{ height: 14 }} />

            <PrimaryButton onClick={openGoogleReviews}>
              Open Google Reviews
            </PrimaryButton>
            {openedGoogle && (
              <p style={{ textAlign: 'center', fontSize: 11.5, color: '#15803d', marginTop: 8 }}>
                Google opened in a new tab — complete your review, then return here.
              </p>
            )}

            <div style={{ height: 16 }} />

            <Card>
              <label
                htmlFor="userReviewLink"
                style={{ display: 'block', fontWeight: 700, fontSize: 13, marginBottom: 8 }}
              >
                Paste your Google share link
              </label>
              <input
                id="userReviewLink"
                type="url"
                value={userReviewLink}
                onChange={(e) => {
                  setUserReviewLink(e.target.value);
                  setLinkError(null);
                }}
                placeholder="https://maps.app.goo.gl/..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid var(--border-color, #E5E2D8)',
                  fontSize: 13,
                  boxSizing: 'border-box',
                }}
              />
              {linkError && (
                <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{linkError}</p>
              )}
              <p style={{ fontSize: 11, color: 'var(--text-muted, #9B8F6E)', marginTop: 8 }}>
                Use the link from Google&apos;s Share option after you post your review.
              </p>
            </Card>

            <div style={{ height: 14 }} />

            <PrimaryButton onClick={handleClaim} disabled={claiming}>
              {claiming ? 'Claiming…' : 'Claim Reward'}
            </PrimaryButton>
            {claimError && (
              <p style={{ textAlign: 'center', color: '#dc2626', fontSize: 12, marginTop: 8 }}>{claimError}</p>
            )}
            <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted, #9B8F6E)', marginTop: 10 }}>
              One Google review reward per customer at this restaurant
            </p>
          </>
        )}

        {claimResult && (
          <GameTodayResultCard
            result={{
              gameType: 'GOOGLE_REVIEW',
              resultSegment: claimResult.resultSegment,
              redemptionCode: claimResult.redemptionCode,
              expiresAt: claimResult.expiresAt,
              userReviewLink: userReviewLink.trim(),
              isWin: true,
            }}
            title="REWARD CLAIMED"
            subtitle="Show your code to staff when you request the bill"
          />
        )}
      </div>
    </div>
  );
};

export default GoogleReviewGame;
